import { cn } from '@/lib/utils'

interface HealthStatusItem {
  id: number
  name: string
  value: string
  status: 'GOOD' | 'GREAT' | 'FAIR' | 'POOR' | 'NONE' | 'AT_RISK'
  description?: string
}

interface HealthStatusGridProps {
  items: HealthStatusItem[]
  title?: string
  columns?: 2 | 3 | 4
  className?: string
}

const statusColors = {
  GOOD: 'bg-risk-good text-white',
  GREAT: 'bg-risk-excellent text-white',
  FAIR: 'bg-risk-fair text-white',
  POOR: 'bg-risk-poor text-white',
  NONE: 'bg-gray-400 text-white',
  AT_RISK: 'bg-risk-critical text-white',
}

const statusBorderColors = {
  GOOD: 'border-l-risk-good',
  GREAT: 'border-l-risk-excellent',
  FAIR: 'border-l-risk-fair',
  POOR: 'border-l-risk-poor',
  NONE: 'border-l-gray-400',
  AT_RISK: 'border-l-risk-critical',
}

export function HealthStatusGrid({
  items,
  title = 'Health Status',
  columns = 2,
  className,
}: HealthStatusGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }

  return (
    <div className={cn('bg-white rounded-xl border shadow-sm overflow-hidden h-full flex flex-col', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between shrink-0">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <div className="flex gap-1.5 text-[10px]">
          <span className="px-1.5 py-0.5 rounded bg-risk-excellent text-white">GREAT</span>
          <span className="px-1.5 py-0.5 rounded bg-risk-good text-white">GOOD</span>
          <span className="px-1.5 py-0.5 rounded bg-risk-fair text-white">FAIR</span>
          <span className="px-1.5 py-0.5 rounded bg-risk-critical text-white">AT RISK</span>
        </div>
      </div>

      {/* Grid - fills remaining space */}
      <div className={cn('grid gap-px p-1 bg-gray-200 flex-1', gridCols[columns])}>
        {items.slice(0, 12).map((item) => (
          <div
            key={item.id}
            className={cn(
              'border-l-4 bg-white px-3 py-2',
              statusBorderColors[item.status]
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-700 font-medium truncate flex-1">
                {item.name}
              </span>
              <span className={cn(
                'text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap',
                statusColors[item.status]
              )}>
                {item.status === 'AT_RISK' ? 'AT RISK' : item.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
